/**
 * 科技蓝模板 - VIP会员页
 */

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { getVipPlans, getSiteSettings } from "@/utils/adminApi";
import TechBlueNavbar from "../components/Navbar";
import TechBlueFooter from "../components/Footer";

// ==================== 支付配置 ====================
interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("读取支付配置失败", e);
    return null;
  }
}

// ==================== 类型 ====================
interface VipPlan {
  id: string;
  name: string;
  price: number;
  original_price: number;
  duration: string;
  badge: string;
  features: string[];
  duration_months: number;
  is_recommended?: boolean;
}

const DEFAULT_PLANS: VipPlan[] = [
  { id: "monthly", name: "月度会员", price: 29, original_price: 49, duration: "1个月", badge: "", duration_months: 1, features: ["信息置顶展示 3次/月", "发布信息不限条数", "专属会员标识", "优先客服支持"] },
  { id: "quarterly", name: "季度会员", price: 79, original_price: 147, duration: "3个月", badge: "热门", duration_months: 3, is_recommended: true, features: ["信息置顶展示 10次/季", "发布信息不限条数", "专属会员标识", "优先客服支持", "数据统计分析"] },
  { id: "yearly", name: "年度会员", price: 199, original_price: 588, duration: "12个月", badge: "超值", duration_months: 12, features: ["信息置顶展示 不限次数", "发布信息不限条数", "专属会员标识", "专属客服支持", "数据统计分析", "优先审核通道", "品牌推广曝光"] },
];

export default function VipPage() {
  const { user } = useAuth();
  const { siteConfig } = useSiteConfig();
  const navigate = useNavigate();
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "";

  const [plans, setPlans] = useState<VipPlan[]>(DEFAULT_PLANS);
  const [enableVip, setEnableVip] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState("quarterly");
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [plansRes, settings, payments] = await Promise.all([
          getVipPlans().catch(() => null),
          getSiteSettings().catch(() => null),
          fetchPaymentConfig(),
        ]);

        if (settings) setEnableVip(settings.enable_vip !== false);

        if (plansRes && Array.isArray(plansRes) && plansRes.length > 0) {
          const formatted = plansRes.map((p: any) => ({
            id: p.id, name: p.name || "VIP套餐", price: p.price || p.amount || 0,
            original_price: p.original_price || p.originalPrice || 0,
            duration: `${p.duration_months || p.duration || 1}个月`,
            badge: p.is_recommended ? "推荐" : (p.badge || ""),
            features: p.benefits || p.features || [],
            duration_months: p.duration_months || p.duration || 1,
            is_recommended: p.is_recommended || false,
          }));
          setPlans(formatted);
          if (formatted.length > 0) setSelectedPlan(formatted.find(p => p.is_recommended)?.id || formatted[0].id);
        }

        if (payments) {
          setPaymentConfig(payments);
          if (payments.qrcode_enabled && payments.qrcode_priority) setPayMethod("qrcode");
        }
      } catch (e) {
        console.error("加载VIP数据失败:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const currentPlan = plans.find(p => p.id === selectedPlan);
  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);

  useEffect(() => {
    if (payMethod === "wechat" && !showWechat) setPayMethod(showAlipay ? "alipay" : "qrcode");
    else if (payMethod === "alipay" && !showAlipay) setPayMethod(showWechat ? "wechat" : "qrcode");
  }, [showWechat, showAlipay]);

  const handlePay = useCallback(() => {
    if (!user) { navigate("/login", { state: { from: "/vip" } }); return; }
    setShowPayModal(true);
  }, [user, navigate]);

  if (!enableVip && !loading) {
    return (
      <div className="min-h-screen bg-white">
        <TechBlueNavbar />
        <div className="flex items-center justify-center py-24">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-vip-crown-line text-4xl text-gray-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-800 mb-3">VIP会员功能已关闭</h1>
            <p className="text-gray-500 mb-6">非常抱歉，VIP会员功能暂时关闭，如有需要请联系管理员。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors">
              <i className="ri-arrow-left-line" />返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <TechBlueNavbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-50 text-cyan-700 rounded-full text-xs font-medium mb-4">
            <i className="ri-vip-crown-line" /> 会员特权
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">选择适合您的会员方案</h1>
          <p className="text-gray-500 max-w-md mx-auto">解锁更多权益，享受更好的服务体验</p>
        </div>

        {/* 定价卡 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <div key={plan.id} className={`relative rounded-2xl border p-6 flex flex-col transition-all hover:shadow-lg ${
              selectedPlan === plan.id || plan.is_recommended
                ? "border-cyan-400 bg-white shadow-md ring-1 ring-cyan-200"
                : "border-gray-200 bg-white"
            }`}>
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-600 to-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {plan.badge}
                </div>
              )}
              <h3 className="text-lg font-bold text-gray-800 mb-1">{plan.name}</h3>
              <div className="mb-4">
                <span className="text-3xl font-bold text-gray-900">¥{plan.price}</span>
                <span className="text-sm text-gray-400 ml-1">/{plan.duration}</span>
                {plan.original_price > plan.price && (
                  <p className="text-xs text-gray-400 mt-1 line-through">原价 ¥{plan.original_price}</p>
                )}
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.length > 0 ? plan.features.map((f: string, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <i className="ri-check-line text-cyan-500 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                )) : <li className="text-sm text-gray-400 text-center">暂未配置权益</li>}
              </ul>
              <button onClick={() => setSelectedPlan(plan.id)}
                className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                  selectedPlan === plan.id
                    ? "bg-gradient-to-r from-cyan-600 to-teal-600 text-white hover:from-cyan-700 hover:to-teal-700 shadow-lg shadow-cyan-500/20"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}>
                {selectedPlan === plan.id ? "✓ 已选择" : "选择此套餐"}
              </button>
            </div>
          ))}
        </div>

        {/* 支付方式 + CTA */}
        <div className="max-w-4xl mx-auto mt-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">选择支付方式</h3>
            <div className="flex flex-wrap gap-3">
              {showWechat && (
                <button onClick={() => setPayMethod("wechat")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    payMethod === "wechat" ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}>
                  <i className="ri-wechat-pay-fill text-lg" />微信支付
                </button>
              )}
              {showAlipay && (
                <button onClick={() => setPayMethod("alipay")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    payMethod === "alipay" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}>
                  <i className="ri-alipay-fill text-lg" />支付宝
                </button>
              )}
              {!showAlipay && !showWechat && (
                <p className="text-sm text-gray-400">暂未配置收款方式</p>
              )}
            </div>
          </div>

          <div className="mt-6 text-center">
            <button onClick={handlePay}
              className="inline-flex items-center gap-3 px-12 py-4 bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-bold rounded-2xl hover:from-cyan-700 hover:to-teal-700 transition-all text-base shadow-xl shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:-translate-y-0.5">
              <i className="ri-vip-crown-line text-xl" />
              <span>立即开通 {currentPlan?.name} — ¥{currentPlan?.price}</span>
            </button>
            <p className="text-xs text-gray-400 mt-3">开通即享全部VIP权益，随时可取消</p>
          </div>
        </div>

        {/* 支付弹窗 */}
        {showPayModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4" onClick={() => setShowPayModal(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-gray-800 text-lg">扫码支付</h3>
                <button onClick={() => setShowPayModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>
              <div className="text-center">
                {payMethod === "alipay" && showAlipay && (
                  <div>
                    <p className="text-xs text-gray-500 mb-3">请使用支付宝扫码付款</p>
                    <img src={paymentConfig?.alipay_qrcode} alt="支付宝收款码" className="w-52 h-52 mx-auto rounded-xl border border-gray-200" />
                  </div>
                )}
                {payMethod === "wechat" && showWechat && (
                  <div>
                    <p className="text-xs text-gray-500 mb-3">请使用微信扫码付款</p>
                    <img src={paymentConfig?.wechat_qrcode} alt="微信收款码" className="w-52 h-52 mx-auto rounded-xl border border-gray-200" />
                  </div>
                )}
                {!showAlipay && !showWechat && (
                  <>
                    <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                      <i className="ri-qr-code-line text-5xl text-gray-300" />
                    </div>
                    <p className="text-sm text-gray-600">支付功能暂未启用</p>
                  </>
                )}
                <p className="text-2xl font-bold text-gray-900 mt-4 mb-1">¥{currentPlan?.price}</p>
                <p className="text-xs text-gray-400">{currentPlan?.name} · {currentPlan?.duration}</p>
              </div>
              <div className="mt-5 p-3.5 bg-cyan-50 rounded-xl text-xs text-cyan-700 text-center font-medium">
                <i className="ri-information-line mr-1" />支付成功后将自动开通VIP权益
              </div>
            </div>
          </div>
        )}
      </div>

      <TechBlueFooter />
    </div>
  );
}
